import {
  Bot,
  Calendar,
  CalendarDays,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Edit3,
  FileText,
  LayoutList,
  ListFilter,
  Loader2,
  Maximize2,
  MessageSquare,
  MoreVertical,
  RotateCcw,
  Search,
  Send,
  Trash2,
  Video as VideoIcon,
  XCircle,
  Zap,
} from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Button,
  Input,
  Modal,
  PageLayout,
  PreviewPostModal,
  SchedulePostModal,
  Select,
  Tooltip,
} from "../components";
import {
  HEADER_ICON,
  HEADER_PRIMARY_BUTTON,
  HEADER_SECONDARY_BUTTON,
  HEADER_SECONDARY_ICON,
} from "../components/layout/headerActionStyles";
import { ScheduleManagerSkeleton } from "../components/skeletons";
import Skeleton from "../components/ui/Skeleton";
import { useActiveContext } from "../contexts/ActiveContextContext";
import { useTranslation } from "../contexts/LanguageContext";
import postsService from "../services/postsService";
import {
  closeLoading,
  showConfirm,
  showLoading,
  showPrompt,
  showSuccess,
  showToastError,
} from "../shared/alertService";
import { getPostImageUrl } from "../shared/constants";
import { cn } from "../utils/cn";
import {
  getPlatformConfig,
  getPlatformName,
  PLATFORMS_LIST,
} from "../utils/platformIcons";

interface PendingPost {
  id: number;
  platform: string;
  content: string;
  scheduled_date?: string;
  scheduled_time?: string;
  content_type?: string;
  theme?: string;
  engagement_prediction?: string;
  hashtags?: string[];
  call_to_action?: string;
  status: string;
  scheduled_at?: string;
  failure_reason?: string;
  image_info?: { filename?: string; public_url?: string };
  video_info?: { filename?: string; public_url?: string };
}

interface ApprovedPost {
  id: number;
  platform: string;
  content: string;
  scheduled_date?: string;
  scheduled_time?: string;
  content_type?: string;
  theme?: string;
  engagement_prediction?: string;
  hashtags?: string[];
  call_to_action?: string;
  status: string;
  approved_at?: string;
  scheduled_at?: string;
  failure_reason?: string;
  image_info?: { filename?: string; public_url?: string };
  video_info?: { filename?: string; public_url?: string };
}

interface ScheduledPost {
  id: number;
  platform: string;
  content: string;
  scheduled_date?: string;
  scheduled_time?: string;
  scheduled_datetime?: string;
  status: string;
  content_type?: string;
  theme?: string;
  engagement_prediction?: string;
  source_type?: string;
  linked_to_schedule?: boolean;
  optimal_timing?: boolean;
  hashtags?: string[];
  call_to_action?: string;
  scheduled_at?: string;
  failure_reason?: string;
  image_info?: { filename?: string; public_url?: string };
  video_info?: { filename?: string; public_url?: string };
}

const ScheduleManager: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeContext } = useActiveContext();
  const [pendingPosts, setPendingPosts] = useState<PendingPost[]>([]);
  const [approvedPosts, setApprovedPosts] = useState<ApprovedPost[]>([]);
  // ADICIONADO: Estado para posts agendados
  const [scheduledPosts, setScheduledPosts] = useState<ScheduledPost[]>([]);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [scheduledLoading, setScheduledLoading] = useState(true);
  const [optimizing, setOptimizing] = useState(false);
  const [, setError] = useState<string | null>(null);
  const [, setSuccess] = useState<string | null>(null);
  const [, setOptimizations] = useState<any[]>([]);
  const [activeView, setActiveView] = useState<"slots" | "agenda">("slots");
  const [, setBestPractices] = useState<string[]>([]);
  /** Modo de exibição: lista (por datas) ou calendário mensal */
  const [displayMode, setDisplayMode] = useState<"list" | "calendar">("list");
  /** Mês atual na visualização calendário */
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());
  /** Modal do dia: data e posts para exibir */
  const [dayModal, setDayModal] = useState<{
    date: string;
    posts: (ScheduledPost | PendingPost)[];
  } | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "scheduled" | "failed"
  >("all");

  // Estados para preview, agendamento e dropdown
  const [showPreviewModal, setShowPreviewModal] = useState<number | null>(null);
  const [showScheduleModal, setShowScheduleModal] = useState<number | null>(
    null,
  );
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [isScheduling, setIsScheduling] = useState(false);
  const [isBankOpen, setIsBankOpen] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const menuRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const buttonRefs = useRef<{ [key: number]: HTMLButtonElement | null }>({});

  const contextId = `${activeContext.type}-${activeContext.organization?.id || ""}-${activeContext.clientCompany?.id || ""}`;
  const contextIdRef = useRef(contextId);

  // Sync ref with current contextId
  contextIdRef.current = contextId;

  // Fechar menu dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuId !== null) {
        const menuRef = menuRefs.current[openMenuId];
        const buttonRef = buttonRefs.current[openMenuId];

        if (
          menuRef &&
          !menuRef.contains(event.target as Node) &&
          buttonRef &&
          !buttonRef.contains(event.target as Node)
        ) {
          setOpenMenuId(null);
        }
      }
    };

    if (openMenuId !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenuId]);

  const loadPendingPosts = useCallback(async () => {
    const requestContextId = contextIdRef.current;
    setPendingLoading(true);
    try {
      const result = await postsService.getPendingPosts(false, 1, 200);
      const posts = result.posts || [];

      // CHECK: If context changed while fetching, ignore results
      if (contextIdRef.current !== requestContextId) {
        return;
      }

      setPendingPosts(posts);
    } catch (error) {
      console.error("❌ Erro ao carregar posts pendentes:", error);
      if (contextIdRef.current === requestContextId) {
        setPendingPosts([]);
        showToastError(t("Não foi possível carregar os posts pendentes."));
      }
    } finally {
      if (contextIdRef.current === requestContextId) {
        setPendingLoading(false);
      }
    }
  }, [activeContext]);

  const loadApprovedPosts = useCallback(async () => {
    const requestContextId = contextIdRef.current;
    // setApprovedLoading(true); // Removido pois não é mais usado para o loading principal
    try {
      const result = await postsService.getApprovedPosts(false, 1, 200);
      const posts = result.posts || [];

      // CHECK: If context changed while fetching, ignore results
      if (contextIdRef.current !== requestContextId) {
        return;
      }

      setApprovedPosts(posts);
    } catch (error) {
      console.error("❌ Erro ao carregar posts aprovados:", error);
      if (contextIdRef.current === requestContextId) {
        setApprovedPosts([]);
        showToastError(t("Não foi possível carregar os posts aprovados."));
      }
    } finally {
      if (contextIdRef.current === requestContextId) {
        // setApprovedLoading(false); // Removido pois não é mais usado para o loading principal
      }
    }
  }, [activeContext]);

  // ADICIONADO: Função para carregar posts agendados
  const loadScheduledPosts = useCallback(async () => {
    const requestContextId = contextIdRef.current;
    setScheduledLoading(true);
    try {
      const result = await postsService.getScheduledPosts(1, 200);
      const posts = result.posts || [];

      // CHECK: If context changed while fetching, ignore results
      if (contextIdRef.current !== requestContextId) {
        return;
      }

      setScheduledPosts(posts);
    } catch (error) {
      console.error("❌ Erro ao carregar posts agendados:", error);
      if (contextIdRef.current === requestContextId) {
        setScheduledPosts([]);
        showToastError(t("Não foi possível carregar os posts agendados."));
      }
    } finally {
      if (contextIdRef.current === requestContextId) {
        setScheduledLoading(false);
      }
    }
  }, [activeContext]);

  const handleDropOnDate = (e: React.DragEvent, dateKey: string) => {
    e.preventDefault();
    const target = e.currentTarget as HTMLElement;
    target.classList.remove(
      "ring-4",
      "ring-purple-400",
      "ring-inset",
      "bg-purple-50",
    );

    const postId = e.dataTransfer.getData("postId");
    const post = approvedPosts.find((p) => p.id.toString() === postId);

    if (!post) return;

    // Abrir o modal de agendamento com a data preenchida
    setScheduleDate(dateKey);
    // Definir horário padrão (ex: 10:00 da manhã)
    setScheduleTime("10:00");
    setShowScheduleModal(post.id);
    setIsBankOpen(false); // Fecha o banco para focar no agendamento
  };

  const handleOpenScheduleModal = (post: any) => {
    setShowScheduleModal(post.id);

    // Se tiver o timestamp completo (scheduled_at), usar para converter para local
    if (post.scheduled_at) {
      const localDate = new Date(post.scheduled_at);
      const y = localDate.getFullYear();
      const m = String(localDate.getMonth() + 1).padStart(2, "0");
      const d = String(localDate.getDate()).padStart(2, "0");
      const hh = String(localDate.getHours()).padStart(2, "0");
      const mm = String(localDate.getMinutes()).padStart(2, "0");

      setScheduleDate(`${y}-${m}-${d}`);
      setScheduleTime(`${hh}:${mm}`);
    } else {
      // Fallback se não tiver o timestamp (usar strings brutas ou padrão)
      const date =
        toDateKey(post.scheduled_date) ||
        new Date().toISOString().split("T")[0];
      setScheduleDate(date);
      setScheduleTime(post.scheduled_time || "12:00");
    }
  };

  const handleConfirmSchedule = async () => {
    if (showScheduleModal === null) return;

    setIsScheduling(true);
    try {
      await postsService.schedulePost(showScheduleModal, {
        date: scheduleDate,
        time: scheduleTime,
      });
      showSuccess(t("Post reagendado com sucesso!"));
      setShowScheduleModal(null);
      loadScheduledPosts();
      loadPendingPosts();
    } catch (error: any) {
      console.error("Erro ao agendar:", error);
      showToastError(
        error.message || t("Não foi possível realizar o agendamento."),
      );
    } finally {
      setIsScheduling(false);
    }
  };

  // Track context to reset state synchronously and avoid flicker
  const [lastContextId, setLastContextId] = useState(contextId);

  if (contextId !== lastContextId) {
    setPendingPosts([]);
    setApprovedPosts([]);
    setScheduledPosts([]);
    setSearchTerm(""); // Limpar busca na troca de contexto
    setPendingLoading(true);
    setScheduledLoading(true);
    setLastContextId(contextId);
  }

  useEffect(() => {
    loadPendingPosts();
    loadApprovedPosts();
    loadScheduledPosts();
  }, [contextId, loadPendingPosts, loadApprovedPosts, loadScheduledPosts]);

  const optimizeSchedule = async () => {
    setOptimizing(true);
    setError(null);
    showLoading(
      t(
        "Nossa IA está analisando seu cronograma para encontrar as melhores oportunidades de engajamento.",
      ),
      t("Otimizando Cronograma"),
    );

    try {
      const result = await postsService.optimizeSchedule({
        business_type: "b2b",
        optimize_timing: true,
        optimize_content: true,
      });

      closeLoading();
      setOptimizations(result.optimizations);
      showSuccess(
        t(
          "Análise concluída! {count} oportunidades de otimização encontradas.",
          { count: result.optimization_opportunities },
        ),
        t("Otimização Concluída"),
      );

      // MOCK: Melhores práticas
      setBestPractices(
        result.best_practices_applied || [
          t("Evite horários de baixo engajamento"),
          t("Diversifique os temas dos posts"),
          t("Use CTAs claros e objetivos"),
          t("Inclua hashtags relevantes"),
        ],
      );
    } catch (error) {
      closeLoading();
      setError(error instanceof Error ? error.message : "Erro desconhecido");
    } finally {
      setOptimizing(false);
    }
  };

  /**
   * Descarta uma falha de postagem (muda status para rejected)
   */
  const handleDismissFailure = async (postId: number) => {
    const confirmed = await showConfirm(
      t(
        "Deseja realmente ignorar este erro? O post sairá da lista de agendados, mas você ainda poderá encontrá-lo no histórico de posts rejeitados.",
      ),
      t("Descartar Erro"),
      t("Sim, descartar"),
      t("Não, manter"),
    );

    if (!confirmed) return;

    showLoading(t("Descartando falha..."));
    try {
      await postsService.dismissPostFailure(postId);
      closeLoading();
      showSuccess(t("Erro descartado com sucesso."), t("Sucesso"));
      loadScheduledPosts(); // Recarregar a lista
    } catch (error) {
      closeLoading();
      console.error("❌ Erro ao descartar falha:", error);
      showToastError(t("Não foi possível descartar a falha do post."));
    }
  };

  const getPlatformColor = (platform: string) => {
    const colors = {
      linkedin: "bg-blue-100 text-blue-800 border-purple-300",
      facebook: "bg-blue-100 text-blue-800 border-purple-300",
      instagram: "bg-pink-100 text-pink-800 border-pink-300",
      google: "bg-green-100 text-green-800 border-green-300",
      tiktok: "bg-gray-100 text-gray-800 border-purple-300",
    };
    return (
      colors[platform.toLowerCase() as keyof typeof colors] ||
      "bg-purple-100 text-purple-800 border-purple-300"
    );
  };

  // Retorna a data zerada em horas para comparação apenas de dia, sem depender
  // de string traduzida (formatDate() já é traduzida e não pode ser comparada
  // com literais como "hoje"/"amanhã" quando o idioma não é PT).
  const parseDateOnly = (dateString: string): Date | null => {
    const dateParts = dateString.split("-");
    let date: Date;
    if (dateParts.length === 3) {
      date = new Date(
        parseInt(dateParts[0]),
        parseInt(dateParts[1]) - 1,
        parseInt(dateParts[2]),
      );
    } else {
      date = new Date(dateString);
    }
    if (isNaN(date.getTime())) return null;
    date.setHours(0, 0, 0, 0);
    return date;
  };

  const isDateToday = (dateString: string | undefined | null): boolean => {
    if (!dateString) return false;
    const date = parseDateOnly(dateString);
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date.getTime() === today.getTime();
  };

  const isDateTomorrow = (dateString: string | undefined | null): boolean => {
    if (!dateString) return false;
    const date = parseDateOnly(dateString);
    if (!date) return false;
    const tomorrow = new Date();
    tomorrow.setHours(0, 0, 0, 0);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return date.getTime() === tomorrow.getTime();
  };

  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return t("Data não disponível");

    try {
      // Criar data com controle de timezone (evitar problemas de UTC)
      const dateParts = dateString.split("-");
      if (dateParts.length === 3) {
        const year = parseInt(dateParts[0]);
        const month = parseInt(dateParts[1]) - 1; // JavaScript months são 0-based
        const day = parseInt(dateParts[2]);
        const date = new Date(year, month, day);

        // Verificar se a data é válida
        if (isNaN(date.getTime())) {
          return t("Data inválida");
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0); // Zerar horário para comparação apenas de data

        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        const targetDate = new Date(date);
        targetDate.setHours(0, 0, 0, 0);

        if (targetDate.getTime() === today.getTime()) {
          return t("hoje");
        } else if (targetDate.getTime() === tomorrow.getTime()) {
          return t("amanhã");
        } else {
          // Formato brasileiro: DD/MM/YYYY
          return date.toLocaleDateString("pt-BR", {
            weekday: "short",
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
        }
      } else {
        // Fallback para formato ISO completo
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          return t("Data inválida");
        }

        return date.toLocaleDateString("pt-BR", {
          weekday: "short",
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      }
    } catch (error) {
      console.error("Erro ao formatar data:", error);
      return "Data inválida";
    }
  };

  // Função adicional para formatar data completa no formato brasileiro
  const formatDateComplete = (dateString: string | undefined | null) => {
    if (!dateString) return t("Data não disponível");

    try {
      // Criar data com controle de timezone
      const dateParts = dateString.split("-");
      if (dateParts.length === 3) {
        const year = parseInt(dateParts[0]);
        const month = parseInt(dateParts[1]) - 1;
        const day = parseInt(dateParts[2]);
        const date = new Date(year, month, day);

        if (isNaN(date.getTime())) {
          return t("Data inválida");
        }

        return date.toLocaleDateString("pt-BR", {
          weekday: "long",
          day: "2-digit",
          month: "long",
          year: "numeric",
        });
      } else {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          return t("Data inválida");
        }

        return date.toLocaleDateString("pt-BR", {
          weekday: "long",
          day: "2-digit",
          month: "long",
          year: "numeric",
        });
      }
    } catch (error) {
      console.error("Erro ao formatar data completa:", error);
      return "Data inválida";
    }
  };

  const handleEditPost = (postId: number) => {
    // CORREÇÃO: Determinar para onde redirecionar baseado no status do post
    const scheduledPost = scheduledPosts.find((p) => p.id === postId);
    const pendingPost = pendingPosts.find((p) => p.id === postId);
    const approvedPost = approvedPosts.find((p) => p.id === postId);

    if (scheduledPost) {
      // Posts agendados (status 'scheduled') - redirecionar para posts aprovados
      navigate(`/posts?filter=approved&highlight=${postId}`);
    } else if (approvedPost) {
      // Posts aprovados - redirecionar para posts aprovados
      navigate(`/posts?filter=approved&highlight=${postId}`);
    } else if (pendingPost) {
      // Posts pendentes - redirecionar para posts pendentes
      navigate(`/posts?filter=pending&highlight=${postId}`);
    } else {
      // Fallback - tentar posts pendentes
      navigate(`/posts?filter=pending&highlight=${postId}`);
    }
  };

  // MODIFICADO: Função para remover agendamento de post (não exclui o post, apenas limpa a data)
  const handleUnschedulePost = async (postId: number) => {
    const confirmed = await showConfirm(
      t(
        "⚠️ Tem certeza que deseja remover o agendamento deste post? O conteúdo continuará disponível na lista de posts para ser reagendado futuramente.",
      ),
      t("Remover agendamento"),
      t("Remover"),
      t("Cancelar"),
    );
    if (!confirmed) {
      return;
    }

    try {
      await postsService.unschedulePost(postId);
      setSuccess(
        t("✅ Agendamento do post {postId} removido com sucesso!", { postId }),
      );
      setOpenMenuId(null);

      loadScheduledPosts();

      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error("❌ Erro ao remover agendamento:", error);
      setError(
        error instanceof Error
          ? error.message
          : t("Erro ao remover agendamento"),
      );

      setTimeout(() => setError(null), 5000);
    }
  };

  // Função para cancelar post pendente
  const handleCancelPost = async (postId: number) => {
    const reason = await showPrompt(
      t("⚠️ Digite o motivo do cancelamento (opcional):"),
      t("Cancelar post"),
      t("Motivo do cancelamento..."),
      "",
      t("Cancelar post"),
      t("Voltar"),
    );
    if (reason === null) return; // Usuário cancelou

    try {
      await postsService.rejectPost(
        postId,
        reason || t("Cancelado pelo usuário"),
      );
      setSuccess(t("✅ Post {postId} cancelado com sucesso!", { postId }));
      setOpenMenuId(null);

      loadPendingPosts();

      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error("❌ Erro ao cancelar post:", error);
      setError(
        error instanceof Error ? error.message : t("Erro ao cancelar post"),
      );
      setTimeout(() => setError(null), 5000);
    }
  };

  // Função para retornar post cancelado ao calendário
  const handleReturnToSchedule = async (post: PendingPost) => {
    if (!post.scheduled_date || !post.scheduled_time) {
      setError(t("Post não possui data e horário agendados."));
      setTimeout(() => setError(null), 5000);
      return;
    }

    // Verificar se a data ainda não passou
    try {
      const scheduledDateTime = post.scheduled_at
        ? new Date(post.scheduled_at)
        : new Date(
            `${toDateKey(post.scheduled_date)}T${post.scheduled_time}:00`,
          );
      const now = new Date();

      if (scheduledDateTime < now) {
        setError(
          t(
            "Não é possível retornar ao calendário: a data agendada já passou.",
          ),
        );
        setTimeout(() => setError(null), 5000);
        return;
      }

      const confirmReturn = await showConfirm(
        t(
          '🔄 Retornar post ao calendário?\n\nData: {date}\nHorário: {time}\n\nO post será reativado e aparecerá na aba "Agendados".',
          { date: formatDate(post.scheduled_date), time: post.scheduled_time },
        ),
        t("Retornar ao calendário"),
        t("Retornar"),
        t("Cancelar"),
      );

      if (!confirmReturn) return;

      await postsService.schedulePost(post.id, {
        scheduled_date: scheduledDateTime.toISOString(),
      });

      setSuccess(
        t("✅ Post {postId} retornou ao calendário com sucesso!", {
          postId: post.id,
        }),
      );

      loadPendingPosts();
      loadScheduledPosts();

      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error("❌ Erro ao retornar post ao calendário:", error);
      setError(
        error instanceof Error
          ? error.message
          : t("Erro ao retornar post ao calendário"),
      );
      setTimeout(() => setError(null), 5000);
    }
  };

  // Função para duplicar um post (repostar) de forma segura via backend
  const handleDuplicatePost = async (post: any, targetPlatform?: string) => {
    const platformName = getPlatformName(targetPlatform || post.platform);

    const confirmDuplicate = await showConfirm(
      targetPlatform
        ? t(
            '🔄 Deseja criar uma cópia deste post para o {platform}?\n\nO conteúdo e mídia serão copiados para um novo post do {platform} no seu "Banco de Aprovados".',
            { platform: platformName },
          )
        : t(
            '🔄 Deseja criar uma cópia deste post para republicação?\n\nUma cópia idêntica será gerada no seu "Banco de Aprovados", pronta para ser agendada novamente.',
          ),
      targetPlatform
        ? t("Agendar no {platform}", { platform: platformName })
        : t("Republicar Post"),
      targetPlatform
        ? t("Copiar para {platform}", { platform: platformName })
        : t("Duplicar e Repostar"),
      t("Cancelar"),
    );

    if (!confirmDuplicate) return;

    try {
      setOptimizing(true);

      // Chama o novo endpoint do backend que cuida de toda a integridade dos dados
      await postsService.duplicatePost(post.id, targetPlatform);

      setSuccess(
        targetPlatform
          ? t(
              "✅ Post copiado para {platform} com sucesso! Verifique seu Banco de Aprovados.",
              { platform: platformName },
            )
          : t(
              "✅ Post duplicado com sucesso! Verifique seu Banco de Aprovados.",
            ),
      );
      setOpenMenuId(null);

      // Recarregar listas
      loadApprovedPosts();

      // Sugestão visual: abrir o banco para mostrar o novo post
      setTimeout(() => setIsBankOpen(true), 500);

      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error("❌ Erro ao duplicar post:", error);
      setError(
        error instanceof Error ? error.message : t("Erro ao duplicar post"),
      );
      setTimeout(() => setError(null), 5000);
    } finally {
      setOptimizing(false);
    }
  };

  // Função para verificar se post está cancelado
  const isPostCancelled = (post: PendingPost) => {
    return post.status === "rejected" || post.status === "cancelled";
  };

  // Função para verificar se data ainda não passou
  const isDateFuture = (post: PendingPost) => {
    if (!post.scheduled_date || !post.scheduled_time) return false;

    try {
      const scheduledDateTime = post.scheduled_at
        ? new Date(post.scheduled_at)
        : new Date(
            `${toDateKey(post.scheduled_date)}T${post.scheduled_time}:00`,
          );
      const now = new Date();
      return scheduledDateTime > now;
    } catch {
      return false;
    }
  };

  // Função para obter post completo para preview
  const getPostForPreview = (postId: number) => {
    const scheduledPost = scheduledPosts.find((p) => p.id === postId);
    const pendingPost = pendingPosts.find((p) => p.id === postId);
    const approvedPost = approvedPosts.find((p) => p.id === postId);

    const post = scheduledPost || approvedPost || pendingPost;
    if (!post) return null;

    return {
      id: post.id,
      content: post.content,
      hashtags: post.hashtags || [],
      call_to_action: post.call_to_action,
      platform: post.platform,
      status: post.status,
      traffic_source: "organic" as const,
      utm_medium: undefined,
      agent_generated: false,
      image_info: (post as any).image_info,
      video_info: (post as any).video_info,
      linkedin_url: (post as any).linkedin_url,
      published_at: (post as any).published_at,
      failure_reason: (post as any).failure_reason,
    };
  };

  /** Retorna a chave YYYY-MM-DD para uma data (string, Date ou qualquer valor) */
  const toDateKey = React.useCallback((d: string | Date | unknown): string => {
    if (d == null) return "";
    if (typeof d === "string") {
      const s = d.trim();
      if (s.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
      const dmY = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/);
      if (dmY) {
        const [, day, month, year] = dmY;
        return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
      }
      try {
        const parsed = new Date(s);
        if (!isNaN(parsed.getTime())) {
          const y = parsed.getFullYear(),
            m = String(parsed.getMonth() + 1).padStart(2, "0"),
            day = String(parsed.getDate()).padStart(2, "0");
          return `${y}-${m}-${day}`;
        }
      } catch {
        /* ignore */
      }
      return s.length >= 10 ? s.slice(0, 10) : "";
    }
    if (d instanceof Date) {
      const y = d.getFullYear(),
        m = String(d.getMonth() + 1).padStart(2, "0"),
        day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    }
    if (typeof d === "number" && !isNaN(d)) return toDateKey(new Date(d));
    return "";
  }, []);

  /** Obtém a chave de data do post (suporta scheduled_date em qualquer formato e camelCase) */
  const getPostDateKey = React.useCallback(
    (p: {
      scheduled_date?: string | Date;
      scheduledDate?: string | Date;
    }): string => {
      const raw = (p as any).scheduled_date ?? (p as any).scheduledDate;
      return toDateKey(raw);
    },
    [toDateKey],
  );

  // MODIFICADO: Função para retornar posts agendados (status 'scheduled') organizados por data
  // Memoizado: só recalcula quando os posts/filtros realmente mudam,
  // em vez de a cada render (era chamada 2x por render antes).
  const scheduledPostsByDate = React.useMemo(() => {
    if (!Array.isArray(scheduledPosts)) {
      console.warn("⚠️ scheduledPosts não é um array:", scheduledPosts);
      return {};
    }

    let filtered = scheduledPosts;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (post) =>
          post.theme?.toLowerCase().includes(term) ||
          post.content_type?.toLowerCase().includes(term) ||
          post.content?.toLowerCase().includes(term) ||
          post.platform?.toLowerCase().includes(term),
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((post) => post.status === statusFilter);
    }

    return filtered
      .filter((post) => post.scheduled_date)
      .sort((a, b) => {
        if (!a.scheduled_date || !b.scheduled_date) return 0;
        const dateA = new Date(a.scheduled_date);
        const dateB = new Date(b.scheduled_date);
        if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) return 0;
        return dateA.getTime() - dateB.getTime();
      })
      .reduce(
        (acc, post) => {
          const date = toDateKey(post.scheduled_date);
          if (!date) return acc;
          if (!acc[date]) acc[date] = [];
          acc[date].push(post);
          return acc;
        },
        {} as Record<string, ScheduledPost[]>,
      );
  }, [scheduledPosts, searchTerm, statusFilter, toDateKey]);

  // Memoizado: idem acima, pra posts pendentes organizados por data.
  const pendingPostsByDate = React.useMemo(() => {
    if (!Array.isArray(pendingPosts)) {
      console.warn("⚠️ pendingPosts não é um array:", pendingPosts);
      return {};
    }

    let filtered = pendingPosts;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (post) =>
          post.theme?.toLowerCase().includes(term) ||
          post.content_type?.toLowerCase().includes(term) ||
          post.content?.toLowerCase().includes(term) ||
          post.platform?.toLowerCase().includes(term),
      );
    }

    return filtered
      .filter((post) => post.scheduled_date)
      .sort((a, b) => {
        if (!a.scheduled_date || !b.scheduled_date) return 0;
        const dateA = new Date(a.scheduled_date);
        const dateB = new Date(b.scheduled_date);
        if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) return 0;
        return dateA.getTime() - dateB.getTime();
      })
      .reduce(
        (acc, post) => {
          const date = toDateKey(post.scheduled_date);
          if (!date) return acc;
          if (!acc[date]) acc[date] = [];
          acc[date].push(post);
          return acc;
        },
        {} as Record<string, PendingPost[]>,
      );
  }, [pendingPosts, searchTerm, toDateKey]);

  // Mapas de lookup por data (dateKey -> posts), construídos uma vez por
  // mudança nos posts. Antes, getPostsForDate refazia um .filter() na lista
  // inteira pra CADA célula do calendário (até ~42x por render); agora é
  // O(1) por célula.
  const scheduledByDateKeyMap = React.useMemo(() => {
    const map: Record<string, ScheduledPost[]> = {};
    (scheduledPosts || []).forEach((p) => {
      const key = getPostDateKey(p);
      if (!key) return;
      if (!map[key]) map[key] = [];
      map[key].push(p);
    });
    return map;
  }, [scheduledPosts, getPostDateKey]);

  const pendingByDateKeyMap = React.useMemo(() => {
    const map: Record<string, PendingPost[]> = {};
    (pendingPosts || []).forEach((p) => {
      const key = getPostDateKey(p);
      if (!key) return;
      if (!map[key]) map[key] = [];
      map[key].push(p);
    });
    return map;
  }, [pendingPosts, getPostDateKey]);

  /** Posts (agendados e/ou pendentes com data) para uma data YYYY-MM-DD. Em calendário, filterBy respeita o filtro ativo (slots = só agendados, agenda = só pendentes). */
  const getPostsForDate = React.useCallback(
    (
      dateKey: string,
      filterBy?: "slots" | "agenda",
    ): (ScheduledPost | PendingPost)[] => {
      let list: (ScheduledPost | PendingPost)[] = [];
      if (filterBy === "agenda") {
        list = pendingByDateKeyMap[dateKey] || [];
      } else if (filterBy === "slots") {
        list = scheduledByDateKeyMap[dateKey] || [];
      } else {
        list = [
          ...(scheduledByDateKeyMap[dateKey] || []),
          ...(pendingByDateKeyMap[dateKey] || []),
        ];
      }

      if (statusFilter !== "all") {
        list = list.filter((p) => p.status === statusFilter);
      }

      return [...list].sort((a, b) => {
        const ta = (a as any).scheduled_time || (a as any).scheduledTime || "";
        const tb = (b as any).scheduled_time || (b as any).scheduledTime || "";
        return String(ta).localeCompare(String(tb));
      });
    },
    [scheduledByDateKeyMap, pendingByDateKeyMap, statusFilter],
  );

  /** Dias do mês para a grade do calendário: primeira linha alinhada ao domingo */
  const calendarDays = React.useMemo((): {
    key: string;
    dateKey: string;
    day: number;
    isCurrentMonth: boolean;
  }[] => {
    const y = calendarMonth.getFullYear();
    const m = calendarMonth.getMonth();
    const first = new Date(y, m, 1);
    const last = new Date(y, m + 1, 0);
    const startPad = first.getDay();
    const daysInMonth = last.getDate();
    const cells: {
      key: string;
      dateKey: string;
      day: number;
      isCurrentMonth: boolean;
    }[] = [];
    for (let i = 0; i < startPad; i++) {
      const d = new Date(y, m, 1 - (startPad - i));
      cells.push({
        key: `pad-${i}`,
        dateKey: toDateKey(d),
        day: d.getDate(),
        isCurrentMonth: false,
      });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      const dateKey = `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      cells.push({ key: dateKey, dateKey, day: d, isCurrentMonth: true });
    }
    const remainder = (7 - ((startPad + daysInMonth) % 7)) % 7;
    for (let i = 0; i < remainder; i++) {
      const d = new Date(y, m + 1, i + 1);
      cells.push({
        key: `next-${i}`,
        dateKey: toDateKey(d),
        day: d.getDate(),
        isCurrentMonth: false,
      });
    }
    return cells;
  }, [calendarMonth, toDateKey]);

  const weekdayLabels = [
    t("Dom"),
    t("Seg"),
    t("Ter"),
    t("Qua"),
    t("Qui"),
    t("Sex"),
    t("Sáb"),
  ];
  const monthLabel = calendarMonth.toLocaleDateString("pt-BR", {
    month: "long",
    year: "numeric",
  });

  return (
    <PageLayout
      title={t("Calendário de Campanhas")}
      subtitle={t(
        "Visualize seu cronograma, ajuste horários e maximize o engajamento com IA.",
      )}
      containerSize="full"
      showContainer={true}
      padding="none"
      className="w-full px-2 py-4"
      loading={scheduledLoading}
      skeleton={<ScheduleManagerSkeleton />}
      actions={
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setIsBankOpen(true)}
            variant="outline"
            size="sm"
            className={HEADER_SECONDARY_BUTTON}
            icon={<LayoutList className={HEADER_SECONDARY_ICON} />}
          >
            <span className="hidden sm:inline text-xs">
              {t("Banco de Aprovados")}
            </span>
            <span className="sm:hidden text-xs">{t("Banco")}</span>
            <span className="ml-1 text-[9px] py-0.5 px-1.5 rounded-full font-bold bg-indigo-100 text-indigo-700">
              {approvedPosts.length}
            </span>
          </Button>

          <Button
            onClick={optimizeSchedule}
            disabled={optimizing || !scheduledPosts.length}
            variant="primary"
            size="sm"
            className={HEADER_PRIMARY_BUTTON}
            icon={
              optimizing ? (
                <Loader2 className={`${HEADER_ICON} animate-spin`} />
              ) : (
                <Zap className={HEADER_ICON} />
              )
            }
          >
            <span className="hidden sm:inline text-xs">
              {t("Otimizar com IA")}
            </span>
            <span className="sm:hidden text-xs">{t("Otimizar")}</span>
          </Button>
        </div>
      }
    >
      <>
        {/* Filter Bar - full-bleed sticky bar, mesmo padrão de "Postagens" */}
        <div className="sticky top-0 z-30 -mt-4 -mx-2 pl-4 pr-2 xl:pl-5 xl:pr-3 py-1.5 bg-white/95 backdrop-blur-md border-b border-purple-200 flex items-center justify-between">
          <div className="w-full">
            <div className="flex flex-row flex-wrap gap-2 sm:gap-2.5 items-center">
              {/* Busca */}
              <div className="flex-1 min-w-[200px] sm:min-w-[250px]">
                <Input
                  type="text"
                  placeholder={t("Pesquisar posts por tema ou conteúdo...")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  inputSize="sm"
                  icon={<Search className="h-4 w-4 text-gray-400" />}
                  className="w-full h-8! sm:h-9!"
                />
              </div>

              {/* Filtros de Visualização (Pills) */}
              <div className="hidden lg:flex items-center gap-2 shrink-0 mx-2">
                <button
                  onClick={() => setActiveView("slots")}
                  className={`h-8 sm:h-9 px-3 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${activeView === "slots" ? "bg-purple-600 text-white shadow-xs" : "bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200"}`}
                >
                  <Calendar className="h-3.5 w-3.5" />
                  {t("Cronograma")}
                  <span
                    className={`text-[10px] sm:text-xs py-0.5 px-2 rounded-full font-bold ${activeView === "slots" ? "bg-white/20 text-white" : "bg-purple-200 text-purple-800"}`}
                  >
                    {Array.isArray(scheduledPosts) ? scheduledPosts.length : 0}
                  </span>
                </button>
                <button
                  onClick={() => setActiveView("agenda")}
                  className={`h-8 sm:h-9 px-3 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${activeView === "agenda" ? "bg-amber-600 text-white shadow-xs" : "bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200"}`}
                >
                  <Clock className="h-3.5 w-3.5" />
                  {t("Sugestões")}
                  {pendingLoading ? (
                    <span
                      className={`inline-block w-4 h-3.5 rounded-full animate-pulse ${activeView === "agenda" ? "bg-white/30" : "bg-amber-300/60"}`}
                    />
                  ) : (
                    <span
                      className={`text-[10px] sm:text-xs py-0.5 px-2 rounded-full font-bold ${activeView === "agenda" ? "bg-white/20 text-white" : "bg-amber-200 text-amber-800"}`}
                    >
                      {Array.isArray(pendingPosts) ? pendingPosts.length : 0}
                    </span>
                  )}
                </button>
              </div>

              {/* Filtro de Status (Apenas para agendados) - wrapper sempre presente pra não deslocar o resto da linha */}
              <div className="hidden lg:block w-[150px] shrink-0 ml-auto">
                <Select
                  value={statusFilter}
                  onChange={(value) => setStatusFilter(value as any)}
                  disabled={activeView !== "slots"}
                  options={[
                    { value: "all", label: t("Tudo"), icon: <ListFilter /> },
                    {
                      value: "scheduled",
                      label: t("Próximos"),
                      icon: <CheckCircle />,
                    },
                    {
                      value: "failed",
                      label: t("Falhas"),
                      icon: <XCircle />,
                    },
                  ]}
                  size="sm"
                  className={`h-8! sm:h-9! ${activeView !== "slots" ? "opacity-40" : ""}`}
                />
              </div>

              {/* Alternar Lista/Mensal */}
              <div
                role="group"
                aria-label={t("Modo de visualização")}
                className="shrink-0 flex items-center gap-1 h-8 sm:h-9 p-0.5 bg-gray-100 rounded-xl border border-purple-200"
              >
                <button
                  onClick={() => setDisplayMode("list")}
                  aria-label={t("Lista")}
                  aria-pressed={displayMode === "list"}
                  className={`flex items-center justify-center h-full aspect-square rounded-lg transition-all ${displayMode === "list" ? "bg-white text-purple-700 shadow-xs ring-1 ring-purple-200" : "text-gray-400 hover:text-gray-600 hover:bg-white/60"}`}
                >
                  <LayoutList className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setDisplayMode("calendar")}
                  aria-label={t("Mensal")}
                  aria-pressed={displayMode === "calendar"}
                  className={`flex items-center justify-center h-full aspect-square rounded-lg transition-all ${displayMode === "calendar" ? "bg-white text-purple-700 shadow-xs ring-1 ring-purple-200" : "text-gray-400 hover:text-gray-600 hover:bg-white/60"}`}
                >
                  <CalendarDays className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Versão mobile dos pills */}
            <div className="lg:hidden flex flex-wrap gap-2 mt-3 pt-3 border-t border-purple-200">
              <button
                onClick={() => setActiveView("slots")}
                className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 ${activeView === "slots" ? "bg-purple-600 text-white" : "bg-purple-50 text-purple-700"}`}
              >
                {t("Cronograma")}{" "}
                <span
                  className={`text-[10px] py-0.5 px-1.5 rounded-full font-bold ${activeView === "slots" ? "bg-white/20 text-white" : "bg-purple-200 text-purple-800"}`}
                >
                  {Array.isArray(scheduledPosts) ? scheduledPosts.length : 0}
                </span>
              </button>
              <button
                onClick={() => setActiveView("agenda")}
                className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 ${activeView === "agenda" ? "bg-amber-600 text-white" : "bg-amber-50 text-amber-700"}`}
              >
                {t("Sugestões")}{" "}
                {pendingLoading ? (
                  <span
                    className={`inline-block w-4 h-3 rounded-full animate-pulse ${activeView === "agenda" ? "bg-white/30" : "bg-amber-300/60"}`}
                  />
                ) : (
                  <span
                    className={`text-[10px] py-0.5 px-1.5 rounded-full font-bold ${activeView === "agenda" ? "bg-white/20 text-white" : "bg-amber-200 text-amber-800"}`}
                  >
                    {Array.isArray(pendingPosts) ? pendingPosts.length : 0}
                  </span>
                )}
              </button>
              <button
                onClick={() => setIsBankOpen(true)}
                className="px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 bg-purple-600 text-white shadow-xs"
              >
                <LayoutList className="h-3 w-3" />
                {t("Banco")}
              </button>
            </div>
          </div>

          {/* Indicador discreto de resultados */}
          {searchTerm && (
            <div className="mt-1 pt-3 border-t border-purple-200 flex items-center justify-between text-[11px] text-gray-500 italic">
              <span>
                {t("Exibindo resultados para:")}{" "}
                <strong className="text-purple-600 font-bold">
                  "{searchTerm}"
                </strong>
              </span>
              <button
                onClick={() => setSearchTerm("")}
                className="text-purple-600 hover:text-purple-800 font-bold underline decoration-dotted"
              >
                {t("Limpar busca")}
              </button>
            </div>
          )}
        </div>

        <div className="w-full flex-1 min-h-[600px] mt-2!">
          {/* Conteúdo: Lista (Agendados/Pendentes) ou Calendário */}
          {displayMode === "list" && activeView === "slots" && (
            <>
              {scheduledPosts.length === 0 ? (
                <div className="text-center py-12 sm:py-20 bg-gray-50/30 rounded-3xl border-2 border-dashed border-purple-200">
                  <div className="inline-flex items-center justify-center p-4 bg-purple-100 rounded-2xl mb-4 text-purple-600">
                    <Calendar className="h-8 w-8 sm:h-10 sm:w-10" />
                  </div>
                  <h3 className="text-base sm:text-xl font-bold text-gray-900 mb-2">
                    {t("Cronograma vazio")}
                  </h3>
                  <p className="max-w-md mx-auto text-sm text-gray-500 mb-8 px-4">
                    {t(
                      "Combine seu planejamento estratégico com nossa IA para criar cronogramas automáticos ou aprove posts pendentes.",
                    )}
                  </p>
                  <Button
                    onClick={() => navigate("/posts?filter=approved")}
                    size="lg"
                    variant="primary"
                    icon={<Calendar className="h-5 w-5" />}
                    className="shadow-lg shadow-purple-500/20"
                  >
                    {t("Ver Posts Aprovados")}
                  </Button>
                </div>
              ) : Object.keys(scheduledPostsByDate).length === 0 &&
                searchTerm ? (
                <div className="text-center py-12 sm:py-20">
                  <div className="inline-flex items-center justify-center p-4 bg-gray-100 rounded-2xl mb-4 text-gray-400">
                    <Search className="h-8 w-8 sm:h-10 sm:w-10" />
                  </div>
                  <h3 className="text-base sm:text-xl font-bold text-gray-900 mb-2">
                    {t("Nenhum resultado encontrado")}
                  </h3>
                  <p className="max-w-md mx-auto text-sm text-gray-500 mb-8 px-4">
                    {t("Não encontramos posts com o termo")}{" "}
                    <span className="font-bold text-purple-600">
                      "{searchTerm}"
                    </span>
                    {t(". Tente pesquisar por outro termo ou limpe a busca.")}
                  </p>
                  <Button
                    onClick={() => setSearchTerm("")}
                    size="md"
                    variant="secondary"
                    icon={<XCircle className="h-4 w-4" />}
                  >
                    {t("Limpar Pesquisa")}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Cronograma por Data - USANDO POSTS AGENDADOS */}
                  {Object.entries(scheduledPostsByDate).map(([date, posts]) => (
                    <div
                      key={date}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.add(
                          "ring-2",
                          "ring-purple-400",
                          "bg-purple-100/30",
                          "scale-[1.01]",
                        );
                      }}
                      onDragLeave={(e) => {
                        e.currentTarget.classList.remove(
                          "ring-2",
                          "ring-purple-400",
                          "bg-purple-100/30",
                          "scale-[1.01]",
                        );
                      }}
                      onDrop={(e) => {
                        handleDropOnDate(e, date);
                        e.currentTarget.classList.remove(
                          "ring-2",
                          "ring-purple-400",
                          "bg-purple-100/30",
                          "scale-[1.01]",
                        );
                      }}
                      className="border border-purple-200 rounded-xl p-3 sm:p-4 bg-linear-to-br from-white to-purple-50/30 hover:shadow-lg hover:border-purple-300 transition-all duration-200"
                    >
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-3 pb-2 border-b border-purple-200">
                        <div className="flex items-center gap-3">
                          <div className="p-2.5 bg-purple-50 border border-purple-200 rounded-xl">
                            <Calendar className="h-5 w-5 text-purple-600" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-base sm:text-lg font-bold text-gray-900 capitalize">
                                {formatDateComplete(date).split(",")[0]}
                                <span className="font-medium text-gray-500 ml-1">
                                  , {formatDateComplete(date).split(",")[1]}
                                </span>
                              </h3>

                              {(isDateToday(date) || isDateTomorrow(date)) && (
                                <Badge
                                  variant={
                                    isDateToday(date) ? "purple" : "secondary"
                                  }
                                  className="uppercase text-[10px] px-2 py-0.5 shadow-xs"
                                >
                                  {formatDate(date)}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs font-medium text-purple-600 bg-purple-50 px-2 py-0.5 rounded-md border border-purple-200">
                                {Array.isArray(posts) ? posts.length : 0}{" "}
                                {t("post")}
                                {posts.length !== 1 ? t("s") : ""}
                              </span>
                              {posts[0]?.scheduled_at && (
                                <span className="flex items-center gap-1 text-xs text-gray-500 italic">
                                  <Clock className="h-3 w-3" /> {t("início às")}{" "}
                                  {new Date(
                                    posts[0].scheduled_at,
                                  ).toLocaleTimeString("pt-BR", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="-mx-1">
                        <table className="w-full text-left border-collapse table-fixed">
                          <tbody>
                            {Array.isArray(posts) &&
                              posts.map((post) => {
                                const handleRowClick = (
                                  e: React.MouseEvent,
                                ) => {
                                  if (
                                    (e.target as HTMLElement).closest(
                                      "button",
                                    ) ||
                                    (e.target as HTMLElement).closest("a") ||
                                    (e.target as HTMLElement).closest(
                                      '[role="menu"]',
                                    ) ||
                                    buttonRefs.current[post.id]?.contains(
                                      e.target as Node,
                                    ) ||
                                    menuRefs.current[post.id]?.contains(
                                      e.target as Node,
                                    )
                                  ) {
                                    return;
                                  }
                                  setShowPreviewModal(post.id);
                                };

                                const platformConfig = getPlatformConfig(
                                  post.platform,
                                );
                                const IconComponent = platformConfig.icon;

                                return (
                                  <tr
                                    key={`approved-${post.id}`}
                                    onClick={handleRowClick}
                                    className={cn(
                                      "group/row border-b border-purple-100 last:border-b-0 hover:bg-purple-50/40 transition-colors cursor-pointer",
                                      openMenuId === post.id
                                        ? "relative z-50"
                                        : "relative z-10",
                                    )}
                                  >
                                    {/* Plataforma + Mídia */}
                                    <td className="px-2 py-2 align-middle w-11">
                                      <div className="relative">
                                        <div
                                          className={`p-1.5 rounded-lg shrink-0 bg-white shadow-xs border border-purple-50 inline-flex ${platformConfig.color || "text-purple-600"}`}
                                        >
                                          <IconComponent className="h-4 w-4" />
                                        </div>
                                        {(post.image_info ||
                                          post.video_info) && (
                                          <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-sm border-2 border-white overflow-hidden shadow-xs bg-gray-100 flex items-center justify-center">
                                            {post.image_info ? (
                                              <img
                                                src={getPostImageUrl(
                                                  post.image_info,
                                                )}
                                                alt={t("Pré-visualização")}
                                                className="w-full h-full object-cover"
                                                onError={(e) =>
                                                  (e.currentTarget.style.display =
                                                    "none")
                                                }
                                              />
                                            ) : (
                                              <VideoIcon className="h-2.5 w-2.5 text-purple-600" />
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </td>

                                    {/* Conteúdo */}
                                    <td className="px-2 py-2 align-middle w-auto">
                                      <h4 className="text-sm font-bold text-gray-900 truncate">
                                        {post.theme ||
                                          post.content_type ||
                                          t("Post Agendado")}
                                      </h4>
                                      <p className="text-xs text-gray-500 truncate italic">
                                        "{post.content}"
                                      </p>
                                      {/* Horário/Status compactos — visíveis apenas em telas pequenas */}
                                      <div className="flex items-center gap-1.5 mt-1 sm:hidden">
                                        <div
                                          className="flex items-center gap-1 text-[10px] font-medium text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded-full border border-purple-200"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditPost(post.id);
                                          }}
                                        >
                                          <Clock className="h-2.5 w-2.5" />
                                          {post.scheduled_at
                                            ? new Date(
                                                post.scheduled_at,
                                              ).toLocaleTimeString("pt-BR", {
                                                hour: "2-digit",
                                                minute: "2-digit",
                                              })
                                            : post.scheduled_time || "--:--"}
                                        </div>
                                        <Badge
                                          variant={
                                            post.status === "failed"
                                              ? "destructive"
                                              : "success"
                                          }
                                          className={`${post.status === "failed" ? "bg-red-50 text-red-700 border-red-200" : "bg-green-50 text-green-700 border-green-200"} text-[10px] py-0 px-1.5 uppercase font-bold tracking-tight`}
                                        >
                                          {post.status === "failed"
                                            ? t("Falha")
                                            : t("Agendado")}
                                        </Badge>
                                      </div>
                                    </td>

                                    {/* Horário */}
                                    <td className="hidden sm:table-cell px-2 py-2 align-middle w-[110px]">
                                      <div
                                        className="flex items-center gap-1 text-[11px] font-medium text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full border border-purple-200 cursor-pointer hover:bg-purple-100 transition-colors w-fit"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEditPost(post.id);
                                        }}
                                        title={t("Editar Post")}
                                      >
                                        <Clock className="h-3 w-3" />
                                        {post.scheduled_at
                                          ? new Date(
                                              post.scheduled_at,
                                            ).toLocaleTimeString("pt-BR", {
                                              hour: "2-digit",
                                              minute: "2-digit",
                                            })
                                          : post.scheduled_time || "--:--"}
                                        <Edit3 className="ml-0.5 h-2.5 w-2.5 opacity-60" />
                                      </div>
                                    </td>

                                    {/* Status */}
                                    <td className="hidden sm:table-cell px-2 py-2 align-middle w-[150px]">
                                      <div className="flex items-center gap-1.5">
                                        <Badge
                                          variant={
                                            post.status === "failed"
                                              ? "destructive"
                                              : "success"
                                          }
                                          className={`${post.status === "failed" ? "bg-red-50 text-red-700 border-red-200" : "bg-green-50 text-green-700 border-green-200"} text-[10px] py-0 px-2 uppercase font-bold tracking-tight`}
                                        >
                                          {post.status === "failed"
                                            ? t("Falha")
                                            : t("Agendado")}
                                        </Badge>
                                        {post.status === "failed" && (
                                          <span
                                            className="text-[11px] text-red-500 truncate max-w-[90px]"
                                            title={
                                              post.failure_reason ||
                                              t("Erro ao publicar")
                                            }
                                          >
                                            {post.failure_reason ||
                                              t("Erro ao publicar")}
                                          </span>
                                        )}
                                      </div>
                                    </td>

                                    {/* Ações */}
                                    <td className="px-2 py-2 align-middle w-[84px] sm:w-[150px]">
                                      <div className="flex items-center justify-end flex-wrap gap-1.5">
                                        <Button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenScheduleModal(post);
                                          }}
                                          size="sm"
                                          variant={
                                            post.status === "failed"
                                              ? "primary"
                                              : "outline"
                                          }
                                          icon={
                                            post.status === "failed" ? (
                                              <Calendar className="h-3.5 w-3.5" />
                                            ) : (
                                              <Send className="h-3.5 w-3.5" />
                                            )
                                          }
                                          className="font-bold shadow-xs"
                                          title={
                                            post.status === "failed"
                                              ? t("Reagendar")
                                              : t("Publicar Agora")
                                          }
                                        />

                                        {post.status === "failed" && (
                                          <Button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDismissFailure(post.id);
                                            }}
                                            size="sm"
                                            variant="ghost"
                                            className="px-2 border border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                                            icon={
                                              <XCircle className="h-4 w-4" />
                                            }
                                            title={t("Ignorar erro")}
                                          />
                                        )}

                                        <div
                                          className="relative"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <button
                                            ref={(el) => {
                                              if (el)
                                                buttonRefs.current[post.id] =
                                                  el;
                                            }}
                                            onClick={() =>
                                              setOpenMenuId(
                                                openMenuId === post.id
                                                  ? null
                                                  : post.id,
                                              )
                                            }
                                            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                                          >
                                            <MoreVertical className="h-4 w-4" />
                                          </button>

                                          {openMenuId === post.id && (
                                            <div
                                              ref={(el) => {
                                                if (el)
                                                  menuRefs.current[post.id] =
                                                    el;
                                              }}
                                              className="absolute right-0 top-full mt-2 w-48 bg-white/95 backdrop-blur-md rounded-xl shadow-2xl border border-purple-200 py-1.5 z-100 animate-in fade-in slide-in-from-top-2 duration-200"
                                            >
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setOpenMenuId(null);
                                                  handleEditPost(post.id);
                                                }}
                                                className="w-full px-4 py-2 text-left text-sm font-medium text-gray-700 hover:bg-purple-50 flex items-center gap-2"
                                              >
                                                <Edit3 className="h-4 w-4 text-purple-600" />{" "}
                                                {t("Editar Post")}
                                              </button>
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setOpenMenuId(null);
                                                  handleOpenScheduleModal(post);
                                                }}
                                                className="w-full px-4 py-2 text-left text-sm font-medium text-gray-700 hover:bg-purple-50 flex items-center gap-2"
                                              >
                                                <Clock className="h-4 w-4 text-indigo-500" />{" "}
                                                {t("Alterar Data/Hora")}
                                              </button>
                                              <div className="h-px bg-gray-100 my-1 mx-2" />
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setOpenMenuId(null);
                                                  handleUnschedulePost(post.id);
                                                }}
                                                className="w-full px-4 py-2 text-left text-sm font-medium text-red-600 hover:bg-red-50 flex items-center gap-2"
                                              >
                                                <Trash2 className="h-4 w-4" />{" "}
                                                {t("Remover Agendamento")}
                                              </button>
                                              <div className="h-px bg-gray-100 my-1 mx-2" />
                                              <div className="px-4 py-1.5 text-[10px] font-bold text-purple-400 uppercase tracking-wider">
                                                {t("Agendar em outra rede")}
                                              </div>
                                              {PLATFORMS_LIST.map(
                                                (platform) => {
                                                  if (
                                                    post.platform ===
                                                    platform.id
                                                  )
                                                    return null;
                                                  const PlatformIconComp =
                                                    platform.icon;
                                                  return (
                                                    <button
                                                      key={platform.id}
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setOpenMenuId(null);
                                                        handleDuplicatePost(
                                                          post,
                                                          platform.id,
                                                        );
                                                      }}
                                                      className={cn(
                                                        "w-full px-4 py-2 text-left text-sm font-medium hover:bg-gray-50 flex items-center gap-2",
                                                        platform.color,
                                                      )}
                                                    >
                                                      <PlatformIconComp className="h-4 w-4" />{" "}
                                                      {platform.name}
                                                    </button>
                                                  );
                                                },
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {displayMode === "list" && activeView === "agenda" && (
            <>
              {pendingLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 animate-pulse">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="bg-white border border-purple-200 rounded-xl p-4 flex flex-col h-full shadow-xs"
                    >
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
                          <Skeleton
                            variant="rectangular"
                            width={60}
                            height={20}
                            className="rounded-full"
                          />
                          <Skeleton
                            variant="rectangular"
                            width={70}
                            height={20}
                            className="rounded-full"
                          />
                        </div>
                        <Skeleton
                          variant="rectangular"
                          width={28}
                          height={28}
                          className="rounded-lg"
                        />
                      </div>

                      <div className="flex-1 space-y-3 mb-4">
                        <div className="bg-gray-50/50 p-2.5 rounded-lg border border-purple-200">
                          <Skeleton
                            variant="text"
                            width="100%"
                            height={12}
                            className="mb-1"
                          />
                          <Skeleton variant="text" width="80%" height={12} />
                        </div>
                      </div>

                      <div className="space-y-3 mt-auto pt-3 border-t border-purple-200">
                        <div className="flex items-center justify-between">
                          <Skeleton variant="text" width={80} height={10} />
                          <Skeleton variant="text" width={40} height={10} />
                        </div>
                        <Skeleton
                          variant="rectangular"
                          width="100%"
                          height={32}
                          className="rounded-lg"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : pendingPosts.length === 0 ? (
                <div className="text-center py-12 sm:py-20 bg-gray-50/30 rounded-3xl border-2 border-dashed border-purple-200">
                  <div className="inline-flex items-center justify-center p-4 bg-amber-100 rounded-2xl mb-4 text-amber-600">
                    <MessageSquare className="h-8 w-8 sm:h-10 sm:w-10" />
                  </div>
                  <h3 className="text-base sm:text-xl font-bold text-gray-900 mb-2">
                    {t("Sem sugestões pendentes")}
                  </h3>
                  <p className="max-w-md mx-auto text-sm text-gray-500 mb-8 px-4">
                    {t(
                      "Excelente! No momento você não tem sugestões aguardando revisão. Que tal gerar novas com IA?",
                    )}
                  </p>
                  <div className="flex flex-col sm:flex-row justify-center gap-3">
                    <Button
                      onClick={() => navigate("/posts/ai-schedule")}
                      size="lg"
                      variant="primary"
                      icon={<Bot className="h-5 w-5" />}
                      className="shadow-lg shadow-purple-500/20"
                    >
                      {t("Cronograma IA")}
                    </Button>
                    <Button
                      onClick={() => navigate("/posts?filter=pending")}
                      size="lg"
                      variant="secondary"
                      icon={<FileText className="h-5 w-5" />}
                    >
                      {t("Ver Todos")}
                    </Button>
                  </div>
                </div>
              ) : Object.keys(pendingPostsByDate).length === 0 && searchTerm ? (
                <div className="text-center py-12 sm:py-20">
                  <div className="inline-flex items-center justify-center p-4 bg-gray-100 rounded-2xl mb-4 text-gray-400">
                    <Search className="h-8 w-8 sm:h-10 sm:w-10" />
                  </div>
                  <h3 className="text-base sm:text-xl font-bold text-gray-900 mb-2">
                    {t("Nenhum resultado encontrado")}
                  </h3>
                  <p className="max-w-md mx-auto text-sm text-gray-500 mb-8 px-4">
                    {t("Não encontramos sugestões pendentes com o termo")}{" "}
                    <span className="font-bold text-purple-600">
                      "{searchTerm}"
                    </span>
                    .
                  </p>
                  <Button
                    onClick={() => setSearchTerm("")}
                    size="md"
                    variant="secondary"
                    icon={<XCircle className="h-4 w-4" />}
                  >
                    {t("Limpar Pesquisa")}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4 sm:space-y-5">
                  {/* Agrupar posts por data */}
                  {Object.entries(pendingPostsByDate).map(([date, posts]) => (
                    <div
                      key={date}
                      className="border border-purple-200 rounded-xl p-4 sm:p-5 bg-linear-to-br from-white to-yellow-50/30 hover:shadow-lg hover:border-purple-300 transition-all duration-200"
                    >
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4 sm:mb-5 pb-3 sm:pb-4 border-b border-purple-200">
                        <div className="flex items-center gap-3">
                          <div className="p-2.5 bg-amber-50 border border-amber-100 rounded-xl">
                            <Calendar className="h-5 w-5 text-amber-600" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-base sm:text-lg font-bold text-gray-900 capitalize">
                                {formatDateComplete(date).split(",")[0]}
                                <span className="font-medium text-gray-500 ml-1">
                                  , {formatDateComplete(date).split(",")[1]}
                                </span>
                              </h3>

                              {(formatDate(date) === "hoje" ||
                                formatDate(date) === "amanhã") && (
                                <Badge
                                  variant="secondary"
                                  className="bg-amber-100 text-amber-700 border-amber-200 uppercase text-[10px] px-2 py-0.5 shadow-xs"
                                >
                                  {formatDate(date)}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md border border-amber-100">
                                {Array.isArray(posts) ? posts.length : 0}{" "}
                                {t("post")}
                                {posts.length !== 1 ? t("s") : ""}
                              </span>
                              <span className="text-xs text-gray-500 italic">
                                {t("Aguardando aprovação")}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="-mx-1">
                        <table className="w-full text-left border-collapse table-fixed">
                          <tbody>
                            {Array.isArray(posts) &&
                              posts.map((post) => {
                                const handleRowClick = (
                                  e: React.MouseEvent,
                                ) => {
                                  if (
                                    (e.target as HTMLElement).closest(
                                      "button",
                                    ) ||
                                    (e.target as HTMLElement).closest("a") ||
                                    (e.target as HTMLElement).closest(
                                      '[role="menu"]',
                                    ) ||
                                    buttonRefs.current[post.id]?.contains(
                                      e.target as Node,
                                    ) ||
                                    menuRefs.current[post.id]?.contains(
                                      e.target as Node,
                                    )
                                  ) {
                                    return;
                                  }
                                  setShowPreviewModal(post.id);
                                };
                                const platformConfig = getPlatformConfig(
                                  post.platform,
                                );
                                const IconComponent = platformConfig.icon;
                                const isCancelled = isPostCancelled(post);

                                return (
                                  <tr
                                    key={`pending-${post.id}`}
                                    onClick={handleRowClick}
                                    className={cn(
                                      "group/row border-b border-purple-100 last:border-b-0 hover:bg-amber-50/40 transition-colors cursor-pointer",
                                      openMenuId === post.id
                                        ? "relative z-50"
                                        : "relative z-10",
                                    )}
                                  >
                                    {/* Plataforma + Mídia */}
                                    <td className="px-2 py-2 align-middle w-11">
                                      <div className="relative">
                                        <div
                                          className={`p-1.5 rounded-lg shrink-0 bg-white shadow-xs border border-purple-50 inline-flex ${platformConfig.color || "text-purple-600"}`}
                                        >
                                          <IconComponent className="h-4 w-4" />
                                        </div>
                                        {(post.image_info ||
                                          post.video_info) && (
                                          <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-sm border-2 border-white overflow-hidden shadow-xs bg-gray-100 flex items-center justify-center">
                                            {post.image_info ? (
                                              <img
                                                src={getPostImageUrl(
                                                  post.image_info,
                                                )}
                                                alt={t("Pré-visualização")}
                                                className="w-full h-full object-cover"
                                                onError={(e) =>
                                                  (e.currentTarget.style.display =
                                                    "none")
                                                }
                                              />
                                            ) : (
                                              <VideoIcon className="h-2.5 w-2.5 text-purple-600" />
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </td>

                                    {/* Conteúdo */}
                                    <td className="px-2 py-2 align-middle w-auto">
                                      <h4 className="text-sm font-bold text-gray-900 truncate">
                                        {post.theme ||
                                          post.content_type ||
                                          t("Post Sugerido")}
                                      </h4>
                                      <p className="text-xs text-gray-500 truncate italic">
                                        "{post.content}"
                                      </p>
                                      {/* Horário/Status compactos — visíveis apenas em telas pequenas */}
                                      <div className="flex items-center gap-1.5 mt-1 sm:hidden">
                                        <div className="flex items-center gap-1 text-[10px] font-medium text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded-full border border-purple-200 font-mono">
                                          <Clock className="h-2.5 w-2.5" />
                                          {post.scheduled_at
                                            ? new Date(
                                                post.scheduled_at,
                                              ).toLocaleTimeString("pt-BR", {
                                                hour: "2-digit",
                                                minute: "2-digit",
                                              })
                                            : post.scheduled_time || "--:--"}
                                        </div>
                                        {isCancelled ? (
                                          <Badge
                                            variant="destructive"
                                            className="bg-red-50 text-red-700 border-red-200 text-[10px] py-0 px-1.5 uppercase font-bold tracking-tight"
                                          >
                                            {t("Cancelado")}
                                          </Badge>
                                        ) : (
                                          <Badge
                                            variant="warning"
                                            className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] py-0 px-1.5 uppercase font-bold tracking-tight"
                                          >
                                            {t("Pendente")}
                                          </Badge>
                                        )}
                                      </div>
                                    </td>

                                    {/* Horário */}
                                    <td className="hidden sm:table-cell px-2 py-2 align-middle w-[100px]">
                                      <div className="flex items-center gap-1 text-[11px] font-medium text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full border border-purple-200 font-mono w-fit">
                                        <Clock className="h-3 w-3" />
                                        {post.scheduled_at
                                          ? new Date(
                                              post.scheduled_at,
                                            ).toLocaleTimeString("pt-BR", {
                                              hour: "2-digit",
                                              minute: "2-digit",
                                            })
                                          : post.scheduled_time || "--:--"}
                                      </div>
                                    </td>

                                    {/* Status */}
                                    <td className="hidden sm:table-cell px-2 py-2 align-middle w-[130px]">
                                      {isCancelled ? (
                                        <Badge
                                          variant="destructive"
                                          className="bg-red-50 text-red-700 border-red-200 text-[10px] py-0 px-2 uppercase font-bold tracking-tight"
                                        >
                                          {t("Cancelado")}
                                        </Badge>
                                      ) : (
                                        <Badge
                                          variant="warning"
                                          className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] py-0 px-2 uppercase font-bold tracking-tight"
                                        >
                                          {t("Pendente")}
                                        </Badge>
                                      )}
                                    </td>

                                    {/* Ações */}
                                    <td className="px-2 py-2 align-middle w-[64px] sm:w-[170px]">
                                      <div className="flex items-center justify-end flex-wrap gap-1.5">
                                        {isCancelled && isDateFuture(post) ? (
                                          <Tooltip content={t("Reativar Post")}>
                                            <Button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleReturnToSchedule(post);
                                              }}
                                              size="sm"
                                              variant="primary"
                                              icon={
                                                <RotateCcw className="h-3.5 w-3.5" />
                                              }
                                              className="font-bold shadow-xs shadow-purple-500/10"
                                              aria-label={t("Reativar Post")}
                                            />
                                          </Tooltip>
                                        ) : (
                                          <Tooltip
                                            content={t("Gerenciar na Fila")}
                                          >
                                            <Button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(
                                                  `/posts?filter=pending&highlight=${post.id}`,
                                                );
                                              }}
                                              size="sm"
                                              variant="outline"
                                              icon={
                                                <MessageSquare className="h-3.5 w-3.5" />
                                              }
                                              className="font-bold border-purple-200 text-purple-700 bg-white"
                                              aria-label={t(
                                                "Gerenciar na Fila",
                                              )}
                                            />
                                          </Tooltip>
                                        )}

                                        <div
                                          className="relative"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <button
                                            ref={(el) => {
                                              if (el)
                                                buttonRefs.current[post.id] =
                                                  el;
                                            }}
                                            onClick={() =>
                                              setOpenMenuId(
                                                openMenuId === post.id
                                                  ? null
                                                  : post.id,
                                              )
                                            }
                                            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                                          >
                                            <MoreVertical className="h-4 w-4" />
                                          </button>

                                          {openMenuId === post.id && (
                                            <div
                                              ref={(el) => {
                                                if (el)
                                                  menuRefs.current[post.id] =
                                                    el;
                                              }}
                                              className="absolute right-0 top-full mt-2 w-48 bg-white/95 backdrop-blur-md rounded-xl shadow-2xl border border-purple-200 py-1.5 z-100 animate-in fade-in slide-in-from-top-2 duration-200"
                                            >
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setOpenMenuId(null);
                                                  handleEditPost(post.id);
                                                }}
                                                className="w-full px-4 py-2 text-left text-sm font-medium text-gray-700 hover:bg-purple-50 flex items-center gap-2"
                                              >
                                                <Edit3 className="h-4 w-4 text-purple-600" />{" "}
                                                {t("Editar Post")}
                                              </button>
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setOpenMenuId(null);
                                                  handleDuplicatePost(post);
                                                }}
                                                className="w-full px-4 py-2 text-left text-sm font-medium text-purple-600 hover:bg-purple-50 flex items-center gap-2"
                                              >
                                                <RotateCcw className="h-4 w-4 text-purple-600" />{" "}
                                                {t("Repostar (Duplicar)")}
                                              </button>
                                              {!isCancelled && (
                                                <>
                                                  <div className="h-px bg-gray-100 my-1 mx-2" />
                                                  <div className="px-4 py-1.5 text-[10px] font-bold text-purple-400 uppercase tracking-wider">
                                                    {t("Agendar em outra rede")}
                                                  </div>
                                                  {PLATFORMS_LIST.map(
                                                    (platform) => {
                                                      if (
                                                        post.platform ===
                                                        platform.id
                                                      )
                                                        return null;
                                                      const PlatformIconComp =
                                                        platform.icon;
                                                      return (
                                                        <button
                                                          key={platform.id}
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            setOpenMenuId(null);
                                                            handleDuplicatePost(
                                                              post,
                                                              platform.id,
                                                            );
                                                          }}
                                                          className={cn(
                                                            "w-full px-4 py-2 text-left text-sm font-medium hover:bg-gray-50 flex items-center gap-2",
                                                            platform.color,
                                                          )}
                                                        >
                                                          <PlatformIconComp className="h-4 w-4" />{" "}
                                                          {platform.name}
                                                        </button>
                                                      );
                                                    },
                                                  )}
                                                  <div className="h-px bg-gray-100 my-1 mx-2" />
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setOpenMenuId(null);
                                                      handleCancelPost(post.id);
                                                    }}
                                                    className="w-full px-4 py-2 text-left text-sm font-medium text-red-600 hover:bg-red-50 flex items-center gap-2"
                                                  >
                                                    <XCircle className="h-4 w-4" />{" "}
                                                    {t("Cancelar Post")}
                                                  </button>
                                                </>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Visualização Calendário mensal */}
          {displayMode === "calendar" && (
            <div className="bg-gray-50/50 rounded-2xl p-3 sm:p-4 border border-purple-200 mt-2">
              {/* Header do Calendário */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2 capitalize">
                    <CalendarDays className="h-6 w-6 text-purple-600" />
                    {monthLabel}
                  </h3>
                  <p className="text-sm text-gray-500 font-medium">
                    {t("Clique em um dia para gerenciar posts")}
                  </p>
                </div>
                <div className="flex items-center bg-white rounded-xl p-1 border border-purple-200 shadow-xs self-start sm:self-auto">
                  <button
                    onClick={() =>
                      setCalendarMonth(
                        new Date(
                          calendarMonth.getFullYear(),
                          calendarMonth.getMonth() - 1,
                          1,
                        ),
                      )
                    }
                    className="p-2 rounded-lg hover:bg-purple-50 text-purple-600 transition-colors"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setCalendarMonth(new Date())}
                    className="px-4 py-1.5 text-sm font-bold text-purple-700 hover:text-purple-800 transition-colors"
                  >
                    {t("Hoje")}
                  </button>
                  <button
                    onClick={() =>
                      setCalendarMonth(
                        new Date(
                          calendarMonth.getFullYear(),
                          calendarMonth.getMonth() + 1,
                          1,
                        ),
                      )
                    }
                    className="p-2 rounded-lg hover:bg-purple-50 text-purple-600 transition-colors"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Grid de Dias da Semana */}
              <div className="grid grid-cols-7 gap-px mb-px bg-purple-100/50 rounded-t-xl overflow-hidden border-t border-x border-purple-200">
                {weekdayLabels.map((day) => (
                  <div
                    key={day}
                    className="bg-white/80 backdrop-blur-xs py-2 text-center text-[10px] font-bold uppercase tracking-widest text-purple-400"
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Grid de Dias */}
              <div className="grid grid-cols-7 gap-px bg-purple-100/50 border-b border-x border-purple-200 rounded-b-xl overflow-hidden">
                {calendarDays.map((cell) => {
                  const filterBy = activeView;
                  const postsForDay = getPostsForDate(cell.dateKey, filterBy);
                  const count = postsForDay.length;
                  const hasPosts = count > 0;
                  const isTodayValue =
                    cell.dateKey === new Date().toISOString().split("T")[0];

                  return (
                    <button
                      key={cell.key}
                      type="button"
                      onClick={() =>
                        setDayModal({
                          date: cell.dateKey,
                          posts: postsForDay ?? [],
                        })
                      }
                      onDragOver={(e) => {
                        e.preventDefault();
                        if (cell.isCurrentMonth) {
                          e.currentTarget.classList.add(
                            "ring-4",
                            "ring-purple-400",
                            "ring-inset",
                            "bg-purple-50",
                          );
                        }
                      }}
                      onDragLeave={(e) => {
                        e.currentTarget.classList.remove(
                          "ring-4",
                          "ring-purple-400",
                          "ring-inset",
                          "bg-purple-50",
                        );
                      }}
                      onDrop={(e) => handleDropOnDate(e, cell.dateKey)}
                      className={`
                                                    min-h-[68px] sm:min-h-[92px] p-2 text-left transition-all duration-300 group relative
                                                    ${cell.isCurrentMonth ? "bg-white hover:bg-purple-50/50 hover:shadow-inner" : "bg-gray-50/40 text-gray-400"}
                                                    ${dayModal?.date === cell.dateKey ? "ring-2 ring-inset ring-purple-500 shadow-xl z-10" : ""}
                                                `}
                    >
                      <div className="flex justify-between items-start mb-1">
                        <span
                          className={`
                                                        inline-flex items-center justify-center h-7 w-7 text-xs font-bold rounded-lg transition-colors
                                                        ${isTodayValue ? "bg-purple-600 text-white shadow-md" : "text-gray-500 group-hover:text-purple-600"}
                                                        ${!cell.isCurrentMonth ? "opacity-50" : ""}
                                                    `}
                        >
                          {cell.day}
                        </span>
                        {hasPosts && (
                          <span className="text-[10px] font-bold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-md border border-purple-100">
                            {postsForDay[0]?.scheduled_at
                              ? new Date(
                                  postsForDay[0].scheduled_at,
                                ).toLocaleTimeString("pt-BR", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                              : (postsForDay[0] as any)?.scheduled_time || "-"}
                          </span>
                        )}
                      </div>

                      <div className="flex flex-col gap-1 mt-1 overflow-hidden">
                        {postsForDay.slice(0, 2).map((post) => {
                          const platformConfig = getPlatformConfig(
                            post.platform,
                          );
                          const Icon = platformConfig.icon;

                          return (
                            <div
                              key={post.id}
                              className={`flex items-center gap-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold truncate border ${
                                post.status === "failed"
                                  ? "bg-red-50 text-red-700 border-red-100/30"
                                  : post.status === "approved" ||
                                      post.status === "APPROVED"
                                    ? "bg-green-50 text-green-700 border-green-100/30"
                                    : "bg-amber-50 text-amber-700 border-amber-100/30"
                              }`}
                            >
                              <Icon
                                className={`h-2.5 w-2.5 ${platformConfig.color || "text-purple-600"}`}
                              />
                              <span className="truncate">
                                {post.theme || post.content_type || t("Post")}
                              </span>
                            </div>
                          );
                        })}
                        {count > 2 && (
                          <div className="text-[9px] font-bold text-purple-400/80 pl-1">
                            + {count - 2} {t("mais...")}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </>

      {/* Modal Posts do dia (ao clicar em um dia no calendário) */}
      {dayModal && (
        <Modal
          open={!!dayModal}
          onClose={() => setDayModal(null)}
          title={t("Posts do dia {date}", { date: formatDate(dayModal.date) })}
          maxWidth="max-w-2xl"
          footer={
            <div className="flex justify-end">
              <Button variant="secondary" onClick={() => setDayModal(null)}>
                {t("Fechar")}
              </Button>
            </div>
          }
        >
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {!(Array.isArray(dayModal.posts) && dayModal.posts.length > 0) ? (
              <p className="text-sm text-gray-500 py-4 text-center">
                {t("Nenhum post nesta data.")}
              </p>
            ) : (
              (dayModal.posts ?? []).map((post) => {
                const isScheduled = (post as any).status === "scheduled";
                const statusLabel = isScheduled ? t("Agendado") : t("Pendente");
                return (
                  <div
                    key={post.id}
                    className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-3 rounded-lg border border-purple-200 bg-purple-50/30 hover:bg-purple-50/50"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPlatformColor(post.platform)}`}
                        >
                          {getPlatformName(post.platform)}
                        </span>
                        <span className="text-xs text-gray-500">
                          {(post as any).scheduled_time || "—"}
                        </span>
                        <span
                          className={`text-xs font-medium px-2 py-0.5 rounded-sm ${isScheduled ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}
                        >
                          {statusLabel}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 line-clamp-2">
                        {post.content.substring(0, 120)}
                        {post.content.length > 120 ? "..." : ""}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="shrink-0"
                        onClick={() => {
                          setShowPreviewModal(post.id);
                          setDayModal(null);
                        }}
                      >
                        {t("Ver preview")}
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        className="shrink-0 text-purple-600"
                        onClick={() => {
                          handleDuplicatePost(post);
                          setDayModal(null);
                        }}
                      >
                        <RotateCcw className="h-3 w-3 mr-1" />
                        {t("Repostar")}
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Modal>
      )}
      {/* Drawer do Banco de Aprovados - Usando Portal para garantir sobreposição total */}
      {createPortal(
        <>
          <div
            className={cn(
              "fixed top-0 right-0 h-full w-80 bg-white shadow-2xl z-1001 transform transition-transform duration-300 ease-in-out border-l border-purple-100 flex flex-col",
              isBankOpen ? "translate-x-0" : "translate-x-full",
            )}
          >
            <div className="p-4 border-b border-purple-50 flex items-center justify-between bg-linear-to-r from-purple-50 to-white">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-purple-100 rounded-lg text-purple-600">
                  <LayoutList className="h-4 w-4" />
                </div>
                <h3 className="font-bold text-gray-900">
                  {t("Banco de Aprovados")}
                </h3>
              </div>
              <button
                onClick={() => setIsBankOpen(false)}
                className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400"
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4 no-scrollbar">
              {approvedPosts.length === 0 ? (
                <div className="text-center py-10 px-4">
                  <div className="mb-4 inline-flex p-4 bg-gray-50 rounded-full">
                    <FileText className="h-8 w-8 text-gray-300" />
                  </div>
                  <p className="text-sm text-gray-500 font-medium">
                    {t("Nenhum post aprovado pronto para agendamento.")}
                  </p>
                </div>
              ) : (
                approvedPosts.map((post) => {
                  const platformConfig = getPlatformConfig(
                    post.platform || "instagram",
                  );
                  const Icon = platformConfig.icon;

                  // Mapeamento preciso de cores por plataforma
                  const brandColors = {
                    facebook: {
                      border: "border-blue-100 hover:border-blue-400",
                      bg: "bg-blue-600",
                      text: "text-blue-600",
                      tag: "text-blue-400",
                    },
                    instagram: {
                      border: "border-purple-100 hover:border-purple-400",
                      bg: "bg-linear-to-tr from-yellow-400 via-red-500 to-purple-600",
                      text: "text-purple-600",
                      tag: "text-purple-400",
                    },
                    linkedin: {
                      border: "border-sky-100 hover:border-sky-400",
                      bg: "bg-blue-700",
                      text: "text-blue-700",
                      tag: "text-sky-400",
                    },
                    google: {
                      border: "border-blue-100 hover:border-blue-400",
                      bg: "bg-[#4285F4]",
                      text: "text-[#4285F4]",
                      tag: "text-[#4285F4]/60",
                    },
                    youtube: {
                      border: "border-red-100 hover:border-red-400",
                      bg: "bg-red-600",
                      text: "text-red-600",
                      tag: "text-red-400",
                    },
                  };

                  const colors =
                    brandColors[post.platform as keyof typeof brandColors] ||
                    brandColors.instagram;

                  return (
                    <div
                      key={post.id}
                      draggable="true"
                      onDragStart={(e) => {
                        e.dataTransfer.setData("postId", post.id.toString());
                        e.dataTransfer.effectAllowed = "move";
                        if (window.navigator.vibrate)
                          window.navigator.vibrate(10);
                      }}
                      className={cn(
                        "group relative p-3.5 bg-white border rounded-2xl transition-all duration-300 cursor-grab active:cursor-grabbing overflow-hidden mb-3 shadow-xs hover:shadow-lg",
                        colors.border,
                      )}
                    >
                      {/* Indicador de Drag (Grip) */}
                      <div className="absolute left-1 top-0 bottom-0 w-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 border-r border-transparent group-hover:border-gray-50 bg-gray-50/0 group-hover:bg-gray-50/50">
                        <div
                          className={cn("flex flex-col gap-0.5", colors.tag)}
                        >
                          <MoreVertical className="h-5 w-5 opacity-40" />
                        </div>
                      </div>

                      <div className="flex items-start gap-4 ml-4">
                        <div className="relative shrink-0">
                          {post.image_info ? (
                            <div className="w-14 h-14 rounded-2xl border-2 border-gray-50 overflow-hidden shadow-inner bg-gray-50 relative">
                              <img
                                src={getPostImageUrl(post.image_info)}
                                alt={t("Post")}
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                onError={(e) =>
                                  (e.currentTarget.style.display = "none")
                                }
                              />
                            </div>
                          ) : (
                            <div
                              className={cn(
                                "w-14 h-14 rounded-2xl flex items-center justify-center text-white shadow-xl ring-4 ring-gray-50",
                                colors.bg,
                              )}
                            >
                              <Icon className="h-7 w-7" />
                            </div>
                          )}

                          {post.image_info && (
                            <div className="absolute -bottom-2 -right-2 p-1.5 bg-white rounded-full shadow-lg border border-purple-600 flex items-center justify-center ring-2 ring-white z-10">
                              <Icon
                                className={cn("h-3.5 w-3.5", colors.text)}
                              />
                            </div>
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <div className="flex items-center gap-2">
                              <span
                                className={cn(
                                  "text-[10px] font-black uppercase tracking-tighter",
                                  colors.tag,
                                )}
                              >
                                #{post.id}
                              </span>
                              <Badge
                                variant="success"
                                className="bg-green-50 text-green-600 border-green-100 text-[8px] sm:text-[9px] py-0 px-1.5 font-black uppercase tracking-widest ring-1 ring-green-100"
                              >
                                {t("Pronto")}
                              </Badge>
                            </div>
                            <button
                              className="p-1.5 rounded-xl text-gray-400 hover:text-purple-600 hover:bg-purple-50 transition-all active:scale-90"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowPreviewModal(post.id);
                              }}
                            >
                              <Maximize2 className="h-4 w-4" />
                            </button>
                          </div>

                          <p className="text-[11px] text-gray-700 line-clamp-2 leading-relaxed font-bold pr-1">
                            {post.content}
                          </p>
                        </div>
                      </div>

                      <div className="mt-4 pt-3 border-t border-gray-50 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div
                            className={cn(
                              "flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest",
                              colors.text,
                            )}
                          >
                            <Icon className="h-3 w-3" />
                            {post.platform
                              ? getPlatformName(post.platform)
                              : t("Social")}
                          </div>
                        </div>
                        <button
                          onClick={() => handleOpenScheduleModal(post)}
                          className={cn(
                            "inline-flex items-center gap-2 text-[11px] font-black px-3 py-2 rounded-xl transition-all active:scale-95 shadow-xs hover:shadow-md uppercase tracking-wider",
                            post.platform === "facebook"
                              ? "bg-blue-50 text-blue-600 hover:bg-blue-100"
                              : post.platform === "instagram"
                                ? "bg-purple-100/10 text-purple-600 hover:bg-purple-100/20 shadow-[inset_0_0_10px_rgba(147,51,234,0.1)] border border-purple-100"
                                : post.platform === "linkedin"
                                  ? "bg-sky-50 text-sky-600 hover:bg-sky-100"
                                  : post.platform === "youtube"
                                    ? "bg-red-50 text-red-600 hover:bg-red-100"
                                    : post.platform === "google_my_business"
                                      ? "bg-blue-50 text-[#4285F4] hover:bg-blue-100"
                                      : "bg-purple-50 text-purple-600 hover:bg-purple-100",
                          )}
                        >
                          <Calendar className="h-3.5 w-3.5" />
                          {t("Agendar")}
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="p-4 bg-gray-50 border-t border-purple-50">
              <p className="text-[10px] text-gray-400 text-center uppercase font-bold tracking-widest">
                Virtus Content Manager
              </p>
            </div>
          </div>

          {/* Removido Overlay de fundo para permitir Arrastar e Soltar no conteúdo atrás */}
        </>,
        document.body,
      )}

      <PreviewPostModal
        open={!!showPreviewModal}
        onClose={() => setShowPreviewModal(null)}
        post={
          showPreviewModal !== null ? getPostForPreview(showPreviewModal) : null
        }
      />

      {/* Modal de Agendamento */}
      <SchedulePostModal
        open={showScheduleModal !== null}
        onClose={() => setShowScheduleModal(null)}
        onConfirm={handleConfirmSchedule}
        loading={isScheduling}
        postId={showScheduleModal}
        scheduleDate={scheduleDate}
        setScheduleDate={setScheduleDate}
        scheduleTime={scheduleTime}
        setScheduleTime={setScheduleTime}
      />
    </PageLayout>
  );
};

export default ScheduleManager;
